'use client'

import { useCallback, useRef, type CSSProperties } from 'react'
import { Transition, type TransitionStatus } from 'react-transition-group'

import { Flex } from '@/styled-system/jsx'

import SearchProvider, { useSearch } from '@/components/search/use-search'
import SearchContent from './header-search-content'

const rootTransitionStyles: Record<TransitionStatus, CSSProperties> = {
  unmounted: { opacity: 0 },
  entering: {
    opacity: 1,
    transitionDuration: '500ms',
  },
  entered: { opacity: 1 },
  exiting: {
    opacity: 0,
    transitionDuration: '250ms',
  },
  exited: { opacity: 0 },
}

const contentTransitionStyles: Record<TransitionStatus, CSSProperties> = {
  unmounted: {
    transform: 'translateY(-64px)',
  },
  entering: {
    transform: 'translateY(0)',
    transitionDuration: '500ms',
  },
  entered: {
    transform: 'translateY(0)',
  },
  exiting: {
    transform: 'translateY(-64px)',
    transitionDuration: '250ms',
  },
  exited: {
    transform: 'translateY(-64px)',
  },
}

export default function HeaderSearch() {
  const { isOpen, onClose } = useSearch()
  const entryRef = useRef<HTMLDivElement>(null)
  const exitRef = useRef<HTMLDivElement>(null)
  const nodeRef = isOpen ? entryRef : exitRef

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') onClose
    },
    [onClose]
  )

  return (
    <SearchProvider>
      <Transition
        nodeRef={nodeRef}
        in={isOpen}
        timeout={{ enter: 350, exit: 250 }}
        unmountOnExit
      >
        {(state) => (
          <Flex
            ref={nodeRef}
            position='absolute'
            inset='auto'
            top={{ base: '-56px', md: '0' }}
            px={{ base: '0', md: '4' }}
            h={{ base: 'calc(100% + 56px)', md: 'full' }}
            w='full'
            zIndex='search'
            style={rootTransitionStyles[state]}
            onKeyDown={handleKeyDown}
          >
            <Flex
              bg='#170D19'
              position='absolute'
              inset='auto'
              top='0'
              left='0'
              opacity='0.7'
              h='full'
              w='full'
              onClick={onClose}
            />

            <SearchContent style={contentTransitionStyles[state]} />
          </Flex>
        )}
      </Transition>
    </SearchProvider>
  )
}
